import React, { useEffect, useState } from 'react';

import Ecommerce from './ecommerce/Ecommerce';
import fallbackDictionary from './fallbackDictionary';
import Regions from './regions/Regions';
import Status from './status/status';
import { AgeGroup, CardControl, CardControlLoader, EcommerceInfo, Piwik, Region, SiteCoreDictionary } from './types';

import './RegionBlockForm.scss';

interface RegionBlockFormProps {
    cardControlLoader: CardControlLoader;
    formRegions: Region[];
    setFormRegions: (regions: Region[]) => void;
    formEnabledEcommerce: boolean;
    setFormEnabledEcommerce: (value: boolean) => void;
    regionBlock: CardControl;
    postRegionBlock: (updatedCardControl: CardControl) => void;
    userAgeGroup: AgeGroup;
    userIsHighFraudRisk?: boolean;
    sitecoreDictionary?: SiteCoreDictionary;
    infoToIncludeEcommerce?: EcommerceInfo;
    piwik: Piwik | undefined;
}

const RegionBlockForm = ({
    cardControlLoader,
    formRegions,
    setFormRegions,
    formEnabledEcommerce,
    setFormEnabledEcommerce,
    regionBlock,
    postRegionBlock,
    userAgeGroup,
    userIsHighFraudRisk,
    sitecoreDictionary,
    infoToIncludeEcommerce,
    piwik,
}: RegionBlockFormProps) => {
    const [showSavingIndicator, setShowSavingIndicator] = useState<boolean>(false);
    const { isPostingCardControl, hasPostedCardControl, failedPostingCardControl } = cardControlLoader;

    const dictionary: SiteCoreDictionary = { ...fallbackDictionary, ...sitecoreDictionary };

    const valuesHaveChanged =
        formEnabledEcommerce !== regionBlock.ecommerce ||
        JSON.stringify(formRegions) !== JSON.stringify(regionBlock.regions);

    const includeEcommerce = typeof infoToIncludeEcommerce !== 'undefined';

    useEffect(() => {
        setShowSavingIndicator(!(hasPostedCardControl || failedPostingCardControl));
    }, [hasPostedCardControl, failedPostingCardControl]);

    useEffect(() => {
        if (failedPostingCardControl) {
            setFormRegions(regionBlock.regions);
            setFormEnabledEcommerce(regionBlock.ecommerce);
        }
    }, [failedPostingCardControl, regionBlock.regions, regionBlock.ecommerce, setFormRegions, setFormEnabledEcommerce]);

    useEffect(() => {
        if (!valuesHaveChanged) {
            return;
        }

        setShowSavingIndicator(true);

        const timeout = setTimeout(() => {
            const updatedCardControl: CardControl = {
                ...regionBlock,
                ecommerce: formEnabledEcommerce,
                regions: formRegions,
            };
            postRegionBlock(updatedCardControl);
        }, 1500);

        // eslint-disable-next-line consistent-return
        return () => clearTimeout(timeout); // Clear existing timer every time it runs.
    }, [formEnabledEcommerce, regionBlock, formRegions, valuesHaveChanged, postRegionBlock]);

    return (
        <div
            className="block-region-wrapper"
            data-track-id={piwik?.regionBlockEcommerce?.regionBlockAndEcommerceForm?.dataTrackId}
            data-track-linkname={piwik?.regionBlockEcommerce?.regionBlockAndEcommerceForm?.dataTrackLinkname}
            id={piwik?.regionBlockEcommerce?.regionBlockAndEcommerceForm?.dataTrackId}
        >
            <section aria-labelledby="Regionsperre">
                {includeEcommerce && (
                    <Ecommerce
                        enabledEcommerce={formEnabledEcommerce}
                        setEnabledEcommerce={setFormEnabledEcommerce}
                        isSaving={isPostingCardControl}
                        userAgeGroup={userAgeGroup}
                        userIsHighFraudRisk={userIsHighFraudRisk}
                        infoToIncludeEcommerce={infoToIncludeEcommerce}
                        dictionary={dictionary}
                        piwik={piwik}
                    />
                )}
                <Regions
                    regions={formRegions}
                    setRegions={setFormRegions}
                    isSaving={isPostingCardControl}
                    userAgeGroup={userAgeGroup}
                    dictionary={dictionary}
                    piwik={piwik}
                />
                <Status
                    valuesHaveChanged={valuesHaveChanged}
                    showSavingIndicator={showSavingIndicator}
                    cardControlLoader={cardControlLoader}
                    piwik={piwik}
                />
            </section>
        </div>
    );
};

export default RegionBlockForm;
